import { NavigateOptions, To } from 'react-router';
import { useNavigate } from './useI18nNavigate';
import { useCallback } from 'react';
import { getPathPrefix } from '../../utils/navigation';

/**
 * react-router-dom의 useNavigate를 확장하여,
 * 환경(VSCode 또는 웹)에 따라 정의된 prefix를 경로 앞에 자동으로 추가하는 커스텀 훅입니다.
 * Router 컨텍스트가 없는 경우 안전하게 처리합니다.
 *
 * @returns {function} prefix가 적용된 navigate 함수
 */
export function usePrefixedNavigate() {
  const navigate = useNavigate();
  const prefixedNavigate = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      
      // 숫자 인자(예: navigate(-1))는 prefix 없이 그대로 전달합니다.
      if (typeof to === 'number') {
        navigate(to);
        return;
      }
      console.log('usePrefixedNavigate', to);
      const prefix = getPathPrefix();

      // prefix가 비어있으면 원본 경로 그대로 사용
      if (!prefix) {
        navigate(to, options);
        return;
      }

      let finalTo: To;

      if (typeof to === 'string') {
        // 문자열 경로 처리
        // 경로가 이미 prefix로 시작하거나 외부 링크(http/https)인 경우 그대로 사용
        if (to.startsWith(prefix + '/') || to.startsWith('http://') || to.startsWith('https://')) {
          finalTo = to;
        } else if (to === prefix) { // prefix 자체로 이동하는 경우
          finalTo = prefix;
        } else if (to === '/') { // 루트 경로로 이동하는 경우 prefix로 변환
          finalTo = prefix;
        } else {
          // prefix와 경로 사이에 '/'가 없으면 추가
          const separator = to.startsWith('/') ? '' : '/';
          finalTo = `${prefix}${separator}${to}`;
        }
      } else {
        // Path 객체 처리 (pathname, search, hash 등 포함)
        let pathname = to.pathname ?? '';
        if (pathname.startsWith(prefix + '/') || pathname.startsWith('http://') || pathname.startsWith('https://')) {
          // 이미 prefix로 시작하거나 외부 링크면 그대로 사용
        } else if (pathname === prefix) {
          // prefix 자체로 이동하는 경우
        } else if (pathname === '/') {
          // 루트 경로로 이동하는 경우 prefix로 변환
          pathname = prefix;
        } else {
          const separator = pathname.startsWith('/') ? '' : '/';
          pathname = `${prefix}${separator}${pathname}`;
        }
        finalTo = { ...to, pathname };
      }
      // console.log('Navigating to (prefixed):', finalTo);
      navigate(finalTo, options);
    },
    [navigate] // navigate 함수가 변경될 때만 콜백 재생성
  );

  return prefixedNavigate;
} 